import {
  COLOR_THEME_LIST,
  DEFAULT_COLOR_THEME,
  DEFAULT_DENSITY,
  DENSITY_LIST,
} from '@qmulus/ui/utils/appearance.ts';
import {
  AxiosError, AxiosResponse,
} from 'axios';
import {
  acceptHMRUpdate, defineStore,
} from 'pinia';

import {
  asyncRequestActionList,
} from './util/request';

import {
  createOrUpdateCustomStorage, getCustomStorage,
} from '@/api/custom-storage';
import {
  useAppearance,
} from '@/composition/use-appearance.ts';
import {
  createDefaultUserSettings,
} from '@/constants/user-settings.ts';
import {
  components,
} from '@/generated/qmulus/openapi.ts';
import {
  EAppId,
} from '@/types/app-type';
import {
  appListMap,
} from '@/types/app-util';
import {
  getDefaultLanguage, languageMap,
} from '@/types/localization-store';
import {
  EVisualisation,
} from '@/types/performance-dashboard';
import {
  TImageAssessmentTest,
} from '@/types/quality-report-type';
import {
  EChartDashboardViewMode,
} from '@/utils/custom-storage/chart-dashboard-settings';
import {
  CHART_ADDITIONAL_SETTINGS, TCustomChartAdditionalSettings,
} from '@/utils/custom-storage/custom-chart-settings';
import {
  ICustomPerformanceDashboardConfig,
} from '@/utils/custom-storage/custom-production-statistics-config';
import {
  ICustomUserSettings,
} from '@/utils/custom-storage/custom-user-settings';
import {
  ICustomStorageItem,
} from '@/utils/custom-storage/export';
import {
  changeFontSizeForEntirePage,
} from '@/utils/font-size.ts';
import {
  setLanguage,
} from '@/utils/language.ts';

export const DEFAULT_USER_SETTINGS: ICustomStorageItem['customUserSettings'] = createDefaultUserSettings({
  locale: getDefaultLanguage(),
  colorTheme: DEFAULT_COLOR_THEME,
  density: DEFAULT_DENSITY,
});

const LOCAL_STORAGE_KEY__CUSTOM_USER_SETTINGS = 'customUserSettings';

function readCustomUserSettingsFromLocalStorage(): ICustomStorageItem['customUserSettings'] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY__CUSTOM_USER_SETTINGS);
    if (raw) {
      return Object.assign({}, DEFAULT_USER_SETTINGS, JSON.parse(raw));
    }
  } catch {
    // ignore SyntaxError – Thrown if the string to parse is not valid JSON.
  }
  return JSON.parse(JSON.stringify(DEFAULT_USER_SETTINGS));
}

function hasCustomUserSettingsInLocalStorage(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY__CUSTOM_USER_SETTINGS) !== null;
  } catch {
    // ignore SecurityError – Thrown if access to the storage is denied.
    return false;
  }
}

function writeCustomUserSettingsToLocalStorage(settings: ICustomStorageItem['customUserSettings']) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY__CUSTOM_USER_SETTINGS, JSON.stringify(settings));
  } catch {
    // ignore QuotaExceededError
    // Thrown if the storage run out of disk quota or the user declined to grant you more space.
  }
}

function applyUserSettings(settings: ICustomUserSettings) {
  changeFontSizeForEntirePage(settings.fontSize || DEFAULT_USER_SETTINGS.fontSize);
  setLanguage(languageMap[settings.locale || DEFAULT_USER_SETTINGS.locale] || getDefaultLanguage());

  // A stored theme may have been removed from the styles since it was saved.
  const appearance = useAppearance();
  appearance.colorTheme.value = COLOR_THEME_LIST.includes(settings.colorTheme)
    ? settings.colorTheme
    : DEFAULT_USER_SETTINGS.colorTheme;
  appearance.density.value = DENSITY_LIST.includes(settings.density)
    ? settings.density
    : DEFAULT_USER_SETTINGS.density;
}

/**
 * Applies the last known user settings from localStorage immediately.
 * Call this once in main.ts before mounting the app to avoid visual jumps
 * (font size, locale) while the API request is in flight.
 */
export function applyLocalStorageUserSettingsEarly() {
  applyUserSettings(readCustomUserSettingsFromLocalStorage());
}

/**
 * Saves of a draft may overlap, e.g. selecting a job right after an edit. Each save is sent once the
 * previous one has finished, so every save reaches the server and the stored draft always ends with
 * the latest state.
 */
function createDraftSaveQueue() {
  let pendingSaves: Promise<unknown> = Promise.resolve();
  return {
    enqueue<T>(save: () => Promise<T>): Promise<T> {
      const queuedSave = pendingSaves.then(save);
      pendingSaves = queuedSave.catch(() => undefined);
      return queuedSave;
    },
    waitForPendingSaves(): Promise<unknown> {
      return pendingSaves;
    },
  };
}

/**
 * Pinned apps used to be stored as a map, which cannot keep the order the user chose. Users who
 * have not pinned an app since then still have only the map, so their pinned apps are taken from it.
 */
async function loadLegacyCustomPinnedAppList(): Promise<AxiosResponse<ICustomStorageItem['customPinnedAppList']>> {
  try {
    const response = await getCustomStorage({
      path: {
        storageKey: 'customPinnedAppMap',
      },
    });
    const pinnedAppList = (Object.keys(response.data) as `${EAppId}`[])
      .filter((appId) => response.data[appId] === true)
      .map((appId) => parseInt(appId, 10) as EAppId);
    return {
      ...response,
      data: pinnedAppList,
    };
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return {
        ...error.response,
        status: 200,
        data: [],
      };
    }
    throw error;
  }
}

const qualityReportDraftSaveQueue = createDraftSaveQueue();
const auditReportDraftSaveQueue = createDraftSaveQueue();

export const useCustomUserStorageStore = defineStore('customUserStorage', {
  state: () => ({
    customUserSettings: readCustomUserSettingsFromLocalStorage() as ICustomStorageItem['customUserSettings'],
    isCustomUserSettingsLoaded: false,
    hasCachedCustomUserSettings: hasCustomUserSettingsInLocalStorage(),
    hasCustomUserSettingsLoadFailed: false,
    customUserSettingsPromise: undefined as Promise<AxiosResponse<ICustomStorageItem['customUserSettings']>> | undefined,
    customChartAdditionalSettings: undefined as TCustomChartAdditionalSettings | undefined,
    customChartAdditionalSettingsPromise: undefined as Promise<AxiosResponse<TCustomChartAdditionalSettings>> | undefined,
    customPerformanceDashboardConfig: undefined as ICustomStorageItem['customPerformanceDashboardConfig'] | undefined,
    customPerformanceDashboardConfigPromise: undefined as Promise<AxiosResponse<ICustomStorageItem['customPerformanceDashboardConfig']>> | undefined,
    customPinnedAppList: undefined as ICustomStorageItem['customPinnedAppList'] | undefined,
    customPinnedAppListPromise: undefined as Promise<AxiosResponse<ICustomStorageItem['customPinnedAppList']>> | undefined,
    qualityReportDraft: undefined as ICustomStorageItem['qualityReportDraft'] | undefined,
    qualityReportDraftPromise: undefined as Promise<AxiosResponse<ICustomStorageItem['qualityReportDraft']>> | undefined,
    auditReportDraft: undefined as ICustomStorageItem['auditReportDraft'] | undefined,
    auditReportDraftPromise: undefined as Promise<AxiosResponse<ICustomStorageItem['auditReportDraft']>> | undefined,
    chartDashboardSettings: undefined as ICustomStorageItem['chartDashboardSettings'] | undefined,
    chartDashboardSettingsPromise: undefined as Promise<AxiosResponse<ICustomStorageItem['chartDashboardSettings']>> | undefined,
    simulatorProfileTableAdditionalColumns: undefined as number[] | undefined,
    simulatorProfileTableAdditionalColumnsPromise: undefined as Promise<AxiosResponse<number[]>> | undefined,
    releaseHighlights: undefined as ICustomStorageItem['releaseHighlights'] | undefined,
    releaseHighlightsPromise: undefined as Promise<AxiosResponse<ICustomStorageItem['releaseHighlights']>> | undefined,
  }),
  getters: {
    isCustomUserSettingsLoading: (state) => state.customUserSettingsPromise !== undefined,
    /**
     * Whether pages can be rendered with the user's settings. Settings cached from a previous
     * visit are applied before the app is mounted; without them, pages would render with the
     * defaults, e.g. dates in the browser language, and not update once the settings arrive.
     */
    areCustomUserSettingsReady: (state) => state.hasCachedCustomUserSettings
      || state.isCustomUserSettingsLoaded
      || state.hasCustomUserSettingsLoadFailed,
    customPerformanceDashboardConfigIsLoading: (state) => state.customPerformanceDashboardConfigPromise !== undefined,
    customPinnedAppListIsLoading: (state) => state.customPinnedAppListPromise !== undefined,
    qualityReportDraftIsLoading: (state) => state.qualityReportDraftPromise !== undefined,
  },
  actions: {
    applyCustomUserSetting(settings: ICustomUserSettings) {
      applyUserSettings(settings);
      writeCustomUserSettingsToLocalStorage(settings);
    },
    async loadAndApplyCustomUserSettings(force: boolean): Promise<ICustomUserSettings> {
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'customUserSettings',
          },
        })
          .catch((error) => {
            if (error instanceof AxiosError
              && error?.response?.status === 404) {
              this.customUserSettings = DEFAULT_USER_SETTINGS;

              error.response!.status = 200;

              error.response!.data = this.customUserSettings;
              return Promise.resolve(error.response!);
            }

            this.hasCustomUserSettingsLoadFailed = true;
            throw error;
          })
          .then((response) => {
            this.applyCustomUserSetting(response.data);
            return response;
          }),
        list: this.customUserSettings,
        promise: this.customUserSettingsPromise,
        setList: (data) => {
          // in case attributes have been added to the settings later, we dynamically
          // patch the settings for users where new attributes are missing.
          this.customUserSettings = Object.assign(Object.assign({}, DEFAULT_USER_SETTINGS), data);
          this.isCustomUserSettingsLoaded = true;
        },
        setPromise: (promise) => {
          this.customUserSettingsPromise = promise;
        },
        force,
      });
    },
    async loadAndApplyCustomUserSettingsOnce() {
      return this.loadAndApplyCustomUserSettings(!this.isCustomUserSettingsLoaded);
    },
    async createOrUpdateCustomUserSettings(config: ICustomStorageItem['customUserSettings']) {
      // prevent loosing of settings attributes if only one setting is updated, but the other settings are not sent in the request
      const mergedConfig: ICustomStorageItem['customUserSettings'] = {
        ...DEFAULT_USER_SETTINGS,
        ...this.customUserSettings,
        ...config,
      };

      return asyncRequestActionList({
        callback: () => createOrUpdateCustomStorage({
          path: {
            storageKey: 'customUserSettings',
          },
          bodyParams: mergedConfig,
        }).then((response) => {
          const patchedResponseData = {
            ...DEFAULT_USER_SETTINGS,
            ...response.data,
          };
          this.applyCustomUserSetting(patchedResponseData);
          return {
            ...response,
            data: patchedResponseData,
          };
        }),
        list: this.customUserSettings,
        promise: this.customUserSettingsPromise,
        setList: (data) => {
          this.customUserSettings = {
            ...DEFAULT_USER_SETTINGS,
            ...data,
          };
        },
        setPromise: (promise) => {
          this.customUserSettingsPromise = promise;
        },
        force: true,
      });
    },
    async loadChartAdditionalSettings() {
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'customChartAdditionalSettings',
          },
        }).catch((error) => {
          if (error instanceof AxiosError
            && error?.response?.status === 404) {
            this.customChartAdditionalSettings = {};

            error.response!.status = 200;

            error.response!.data = this.customChartAdditionalSettings;
            return Promise.resolve(error.response!);
          }

          throw error;
        }),
        list: this.customChartAdditionalSettings,
        promise: this.customChartAdditionalSettingsPromise,
        setList: (data) => {
          this.customChartAdditionalSettings = data;
        },
        setPromise: (promise) => {
          this.customChartAdditionalSettingsPromise = promise;
        },
      });
    },
    async createOrUpdateChartAdditionalSettings(settingName: typeof CHART_ADDITIONAL_SETTINGS[number], settingValue: boolean) {
      const currentSettings = JSON.parse(JSON.stringify(this.customChartAdditionalSettings));
      currentSettings[settingName] = settingValue;
      return asyncRequestActionList({
        callback: () => createOrUpdateCustomStorage({
          path: {
            storageKey: 'customChartAdditionalSettings',
          },
          bodyParams: currentSettings,
        }),
        list: this.customChartAdditionalSettings,
        promise: this.customChartAdditionalSettingsPromise,
        setList: (data) => {
          this.customChartAdditionalSettings = data;
        },
        setPromise: (promise) => {
          this.customChartAdditionalSettingsPromise = promise;
        },
        force: true,
      });
    },
    async loadCustomPinnedAppList() {
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'customPinnedAppList',
          },
        })
          .catch((error) => {
            if (error instanceof AxiosError
              && error?.response?.status === 404) {
              return loadLegacyCustomPinnedAppList();
            }

            throw error;
          })
          .then((response) => {
            response.data = response.data.filter((appId) => appListMap[appId as EAppId] !== undefined);
            return response;
          }),
        list: this.customPinnedAppList,
        promise: this.customPinnedAppListPromise,
        setList: (data) => {
          this.customPinnedAppList = data;
        },
        setPromise: (promise) => {
          this.customPinnedAppListPromise = promise;
        },
      });
    },
    async createOrUpdateCustomPinnedAppList(pinnedAppList: ICustomStorageItem['customPinnedAppList']) {
      return asyncRequestActionList({
        callback: () => createOrUpdateCustomStorage({
          path: {
            storageKey: 'customPinnedAppList',
          },
          bodyParams: pinnedAppList,
        }),
        list: this.customPinnedAppList,
        promise: this.customPinnedAppListPromise,
        setList: (data) => {
          this.customPinnedAppList = data;
        },
        setPromise: (promise) => {
          this.customPinnedAppListPromise = promise;
        },
        force: true,
      });
    },
    async toggleCustomPinnedApp(appId: components['schemas']['AppId']) {
      const pinnedAppList = await this.loadCustomPinnedAppList();
      const updatedPinnedAppList = pinnedAppList.includes(appId)
        ? pinnedAppList.filter((pinnedAppId) => pinnedAppId !== appId)
        : [
          ...pinnedAppList,
          appId,
        ];
      await this.createOrUpdateCustomPinnedAppList(updatedPinnedAppList);
    },
    async loadCustomPerformanceDashboardConfig() {
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'customPerformanceDashboardConfig',
          },
        })
          .catch((error) => {
            if (error instanceof AxiosError
              && error?.response?.status === 404) {
              this.customPerformanceDashboardConfig = {
                panelList: Object.values(EVisualisation),
              };

              error.response!.status = 200;

              error.response!.data = this.customPerformanceDashboardConfig;
              return Promise.resolve(error.response!);
            }

            throw error;
          }),
        list: this.customPerformanceDashboardConfig,
        promise: this.customPerformanceDashboardConfigPromise,
        setList: (data) => {
          this.customPerformanceDashboardConfig = data;
        },
        setPromise: (promise) => {
          this.customPerformanceDashboardConfigPromise = promise;
        },
      });
    },
    async createOrUpdateCustomPerformanceDashboardConfig(config: ICustomPerformanceDashboardConfig) {
      return asyncRequestActionList({
        callback: () => createOrUpdateCustomStorage({
          path: {
            storageKey: 'customPerformanceDashboardConfig',
          },
          bodyParams: config,
        }),
        list: this.customPerformanceDashboardConfig,
        promise: this.customPerformanceDashboardConfigPromise,
        setList: (data) => {
          this.customPerformanceDashboardConfig = data;
        },
        setPromise: (promise) => {
          this.customPerformanceDashboardConfigPromise = promise;
        },
        force: true,
      });
    },
    async loadQualityReportDraft(): Promise<ICustomStorageItem['qualityReportDraft']> {
      await qualityReportDraftSaveQueue.waitForPendingSaves();
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'qualityReportDraft',
          },
        })
          .catch((error) => {
            if (error instanceof AxiosError
              && error?.response?.status === 404) {
              this.qualityReportDraft = {};

              error.response!.status = 200;

              error.response!.data = this.qualityReportDraft;
              return Promise.resolve(error.response!);
            }

            throw error;
          }),
        list: this.qualityReportDraft,
        promise: this.qualityReportDraftPromise,
        setList: (data) => {
          this.qualityReportDraft = data;
        },
        setPromise: (promise) => {
          this.qualityReportDraftPromise = promise;
        },
      });
    },
    async createOrUpdateQualityReportDraft(
      config: ICustomStorageItem['qualityReportDraft'],
    ) {
      const bodyParams: ICustomStorageItem['qualityReportDraft'] = {
        ...config,
        samples: config.samples
          ? [
            ...config.samples.map((_sample) => ({
              ..._sample,
              tests: _sample.tests
                ? _sample.tests.map((_test) => {
                  // there is internal property which is used for validation progress state
                  // this property, if saved on BE, can hard-lock UI from user

                  delete _test.isLoading;
                  if (
                    _test.testType === 'image'
                    || _test.testType === 'file'
                  ) {
                    // @ts-expect-error: we need it in order to add "invalid" fileData property
                    return {
                      ..._test,
                      // we delete fileData, because that data will be saved in DB,
                      // and b64 file might be HUGE
                      fileData: undefined,
                    } as TImageAssessmentTest;
                  }
                  return _test;
                })
                : [],
            })),
          ]
          : undefined,
      };
      const response = await qualityReportDraftSaveQueue.enqueue(() => createOrUpdateCustomStorage({
        path: {
          storageKey: 'qualityReportDraft',
        },
        bodyParams,
      }));
      this.qualityReportDraft = response.data;
      return response.data;
    },
    async loadAuditReportDraft(): Promise<ICustomStorageItem['auditReportDraft']> {
      await auditReportDraftSaveQueue.waitForPendingSaves();
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'auditReportDraft',
          },
        })
          .catch((error) => {
            if (error instanceof AxiosError
              && error?.response?.status === 404) {
              this.auditReportDraft = {};

              error.response!.status = 200;

              error.response!.data = this.auditReportDraft;
              return Promise.resolve(error.response!);
            }

            throw error;
          }),
        list: this.auditReportDraft,
        promise: this.auditReportDraftPromise,
        setList: (data) => {
          this.auditReportDraft = data;
        },
        setPromise: (promise) => {
          this.auditReportDraftPromise = promise;
        },
      });
    },
    async createOrUpdateAuditReportDraft(
      config: ICustomStorageItem['auditReportDraft'],
    ) {
      const response = await auditReportDraftSaveQueue.enqueue(() => createOrUpdateCustomStorage({
        path: {
          storageKey: 'auditReportDraft',
        },
        bodyParams: config,
      }));
      this.auditReportDraft = response.data;
      return response.data;
    },
    async loadChartDashboardSettings(): Promise<ICustomStorageItem['chartDashboardSettings']> {
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'chartDashboardSettings',
          },
        })
          .catch((error) => {
            if (error instanceof AxiosError
              && error?.response?.status === 404) {
              this.chartDashboardSettings = {
                viewMode: EChartDashboardViewMode.EXPERT,
              };

              error.response!.status = 200;

              error.response!.data = this.chartDashboardSettings;
              return Promise.resolve(error.response!);
            }

            throw error;
          }),
        list: this.chartDashboardSettings,
        promise: this.chartDashboardSettingsPromise,
        setList: (data) => {
          this.chartDashboardSettings = data;
        },
        setPromise: (promise) => {
          this.chartDashboardSettingsPromise = promise;
        },
      });
    },
    async createOrUpdateloadChartDashboardSettings(
      config: ICustomStorageItem['chartDashboardSettings'],
    ) {
      return asyncRequestActionList({
        callback: () => createOrUpdateCustomStorage({
          path: {
            storageKey: 'chartDashboardSettings',
          },
          bodyParams: config,
        }),
        list: this.chartDashboardSettings,
        promise: this.chartDashboardSettingsPromise,
        setList: (data) => {
          this.chartDashboardSettings = data;
        },
        setPromise: (promise) => {
          this.chartDashboardSettingsPromise = promise;
        },
        force: true,
      });
    },
    async loadSimulatorProfileTableAdditionalColumns() {
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'simulatorProfileTableAdditionalColumns',
          },
        })
          .catch((error) => {
            error.response!.data = [];
            if (error instanceof AxiosError && error?.response?.status === 404) {
              error.response!.status = 200;
              return Promise.resolve(error.response!);
            }

            throw error;
          }),
        list: this.simulatorProfileTableAdditionalColumns,
        promise: this.simulatorProfileTableAdditionalColumnsPromise,
        setList: (data) => {
          this.simulatorProfileTableAdditionalColumns = data;
        },
        setPromise: (promise) => {
          this.simulatorProfileTableAdditionalColumnsPromise = promise;
        },
      });
    },
    async createOrUpdateSimulatorProfileTableAdditionalColumns(
      config: ICustomStorageItem['simulatorProfileTableAdditionalColumns'],
    ) {
      return asyncRequestActionList({
        callback: () => createOrUpdateCustomStorage({
          path: {
            storageKey: 'simulatorProfileTableAdditionalColumns',
          },
          bodyParams: config,
        }),
        list: this.simulatorProfileTableAdditionalColumns,
        promise: this.simulatorProfileTableAdditionalColumnsPromise,
        setList: (data) => {
          this.simulatorProfileTableAdditionalColumns = data;
        },
        setPromise: (promise) => {
          this.simulatorProfileTableAdditionalColumnsPromise = promise;
        },
        force: true,
      });
    },
    async loadReleaseHighlights(): Promise<ICustomStorageItem['releaseHighlights']> {
      return asyncRequestActionList({
        callback: () => getCustomStorage({
          path: {
            storageKey: 'releaseHighlights',
          },
        })
          .catch((error) => {
            if (error instanceof AxiosError
              && error?.response?.status === 404) {
              error.response!.status = 200;
              error.response!.data = {};
              return Promise.resolve(error.response!);
            }

            throw error;
          }),
        list: this.releaseHighlights,
        promise: this.releaseHighlightsPromise,
        setList: (data) => {
          this.releaseHighlights = data;
        },
        setPromise: (promise) => {
          this.releaseHighlightsPromise = promise;
        },
      });
    },
    async createOrUpdateReleaseHighlights(
      settings: ICustomStorageItem['releaseHighlights'],
    ) {
      return asyncRequestActionList({
        callback: () => createOrUpdateCustomStorage({
          path: {
            storageKey: 'releaseHighlights',
          },
          bodyParams: settings,
        }),
        list: this.releaseHighlights,
        promise: this.releaseHighlightsPromise,
        setList: (data) => {
          this.releaseHighlights = data;
        },
        setPromise: (promise) => {
          this.releaseHighlightsPromise = promise;
        },
        force: true,
      });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCustomUserStorageStore, import.meta.hot));
}
